import { Agent } from "@/types/agent/agent";
import { DEFAULT_BRANDING } from "@/types/ui/branding";

/**
 * The genie's own brand tokens, resolved the same way the rest of the app
 * resolves them (My Genies cards, GenieHorizontalSelector, BrandedQRCode).
 * Everything falls back to the Help Genie brand blue, so a genie with no
 * branding row still tints correctly instead of rendering transparent.
 */
export interface GenieBrand {
  primary: string;
  secondary: string | null;
  gradientEnabled: boolean;
  gradientAngle: number;
  logoUrl: string | null;
  hasCustomBranding: boolean;
  /** `linear-gradient(...)` when a gradient is set, otherwise the flat primary. */
  fill: string;
}

export const resolveGenieBrand = (genie: Agent | null | undefined): GenieBrand => {
  const branding = genie?.branding ?? null;
  const primary: string = branding?.primaryColor || DEFAULT_BRANDING.primaryColor;
  const secondary: string | null = branding?.secondaryColor || null;
  const gradientAngle: number = branding?.gradientAngle || 135;
  const gradientEnabled = Boolean(branding?.gradientEnabled && secondary);
  const logoUrl: string | null = branding?.logoUrl || branding?.customAvatarUrl || null;

  const hasCustomBranding = Boolean(
    branding &&
      (primary !== DEFAULT_BRANDING.primaryColor ||
        secondary ||
        logoUrl ||
        branding.gradientEnabled),
  );

  return {
    primary,
    secondary,
    gradientEnabled,
    gradientAngle,
    logoUrl,
    hasCustomBranding,
    fill: gradientEnabled
      ? `linear-gradient(${gradientAngle}deg, ${primary}, ${secondary})`
      : primary,
  };
};

/** Hex colour + alpha suffix, e.g. `tint("#4E9CFF", "1a")`. */
export const tint = (hex: string, alpha: string) => `${hex}${alpha}`;
